import React, { useState } from 'react';
import { X, Send } from 'lucide-react';
import Button from './Button';
import TextArea from './TextArea';
import Input from './Input';
import { sendDocumentByEmail } from '../../lib/email';

interface EmailDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentNumber: string;
  documentType: 'invoice' | 'quote';
  clientEmail: string;
  clientName: string;
  onSuccess: () => void;
  documentId: string;
}

const EmailDocumentModal: React.FC<EmailDocumentModalProps> = ({
  isOpen,
  onClose,
  documentNumber,
  documentType,
  clientEmail,
  clientName,
  onSuccess,
  documentId,
}) => {
  const [subject, setSubject] = useState(`${documentType === 'invoice' ? 'Facture' : 'Devis'} #${documentNumber}`);
  const [message, setMessage] = useState(
    `Bonjour ${clientName},\n\nVeuillez trouver ci-joint ${documentType === 'invoice' ? 'la facture' : 'le devis'} #${documentNumber}.\n\nCordialement,`
  );
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSending(true);

    try {
      if (!documentId || !clientEmail) {
        throw new Error('Missing required information');
      }

      await sendDocumentByEmail(documentId, clientEmail, subject, message);
      onSuccess();
      onClose();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Erreur lors de l\'envoi de l\'email');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
        
        <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-xl font-semibold">Envoyer par email</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 rounded-md bg-error-50 p-3 text-error-500 dark:bg-error-900/20">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="À"
              value={clientEmail}
              disabled
            />

            <Input
              label="Sujet"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
            />

            <TextArea
              label="Message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              required
            />

            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isSending}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                isLoading={isSending}
                leftIcon={<Send className="h-4 w-4" />}
              >
                Envoyer
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EmailDocumentModal;